import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pos`;

const CustomerSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const isOwner = (localStorage.getItem('pos_user_type') || 'owner') === 'owner';

  // ----- Reset Balances modal state -----
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetScope, setResetScope] = useState('all'); // 'all' | 'selected'
  const [resetMode, setResetMode] = useState('adjustment'); // 'adjustment' | 'wipe'
  const [resetClearPending, setResetClearPending] = useState(true);
  const [resetSelectedIds, setResetSelectedIds] = useState({}); // { customerId: true }
  const [resetSearch, setResetSearch] = useState('');
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [resetSubmitting, setResetSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: '',
    gst_number: '',
    address: '',
    pricing_level: 1,
    opening_balance: 0
  });

  useEffect(() => {
    fetchCustomers();
  }, [search]);

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/customers`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { search: search || undefined }
      });
      setCustomers(response.data);
    } catch (err) {
      setError('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('pos_token');
      
      // Convert empty strings to null for optional fields
      const customerData = {
        name: formData.name,
        mobile: formData.mobile,
        email: formData.email || null,
        gst_number: formData.gst_number || null,
        address: formData.address || null,
        pricing_level: parseInt(formData.pricing_level) || 1,
        opening_balance: parseFloat(formData.opening_balance) || 0
      };
      
      if (editingId) {
        await axios.put(`${API}/customers/${editingId}`, customerData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Customer updated successfully');
      } else {
        await axios.post(`${API}/customers`, customerData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Customer created successfully');
      }
      
      fetchCustomers();
      resetForm();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save customer');
    }
  };

  const handleEdit = (customer) => {
    if (customer.is_guest) {
      setError('Cannot edit guest customer');
      return;
    }
    setFormData({
      name: customer.name,
      mobile: customer.mobile,
      email: customer.email || '',
      gst_number: customer.gst_number || '',
      address: customer.address || '',
      pricing_level: customer.pricing_level || 1,
      opening_balance: customer.opening_balance || 0,
      balance_locked: customer.balance_locked || false
    });
    setEditingId(customer.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;

    try {
      const token = localStorage.getItem('pos_token');
      await axios.delete(`${API}/customers/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Customer deleted successfully');
      fetchCustomers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete customer');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      mobile: '',
      email: '',
      gst_number: '',
      address: '',
      pricing_level: 1,
      opening_balance: 0,
      balance_locked: false
    });
    setEditingId(null);
    setShowForm(false);
  };

  const openResetModal = () => {
    setResetScope('all');
    setResetMode('adjustment');
    setResetClearPending(true);
    setResetSelectedIds({});
    setResetSearch('');
    setResetConfirmOpen(false);
    setShowResetModal(true);
  };

  const nonGuestCustomers = customers.filter(c => !c.is_guest);
  const resetFilteredCustomers = nonGuestCustomers.filter(c =>
    !resetSearch ||
    c.name.toLowerCase().includes(resetSearch.toLowerCase()) ||
    (c.mobile || '').includes(resetSearch)
  );
  const resetSelectedCount = Object.values(resetSelectedIds).filter(Boolean).length;
  const resetTargetCount = resetScope === 'all' ? nonGuestCustomers.length : resetSelectedCount;

  const toggleResetSelect = (id) => {
    setResetSelectedIds(prev => ({ ...prev, [id]: !prev[id] }));
  };
  const selectAllReset = () => {
    setResetSelectedIds(resetFilteredCustomers.reduce((acc, c) => ({ ...acc, [c.id]: true }), {}));
  };
  const deselectAllReset = () => setResetSelectedIds({});

  const submitReset = async () => {
    setResetSubmitting(true);
    setError('');
    setSuccess('');
    try {
      const token = localStorage.getItem('pos_token');
      const body = {
        customer_ids: resetScope === 'selected'
          ? Object.entries(resetSelectedIds).filter(([, v]) => v).map(([k]) => k)
          : null,
        mode: resetMode,
        clear_pending: resetClearPending,
      };
      const response = await axios.post(`${API}/customers/reset-balances`, body, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const { counts } = response.data;
      setSuccess(
        `Reset ${counts.customers_reset} customer(s). ` +
        (resetMode === 'wipe'
          ? `Ledger rows deleted: ${counts.ledger_rows_deleted}.`
          : `Adjustments posted: ${counts.ledger_adjustments_added}.`) +
        (resetClearPending ? ` Pending cleared: ${counts.pending_payments_cleared}.` : '')
      );
      setShowResetModal(false);
      setResetConfirmOpen(false);
      fetchCustomers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset balances');
    } finally {
      setResetSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="customer-settings-page" className="max-w-5xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/gposp/settings')}
          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          data-testid="back-button"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-800">Customers</h1>
          <p className="text-slate-500">Manage customer information</p>
        </div>
        {isOwner && (
          <button
            onClick={openResetModal}
            className="px-4 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 transition-colors flex items-center gap-2 text-sm font-medium"
            data-testid="reset-balances-button"
            title="Reset current + all-time balance to 0 for all or selected customers"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Reset Balances
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700">
          {success}
        </div>
      )}

      {/* Search and Add */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search by name or mobile..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
            data-testid="search-customers"
          />
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="px-6 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors flex items-center gap-2 whitespace-nowrap"
            data-testid="add-customer-button"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Customer
          </button>
        )}
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">
            {editingId ? 'Edit Customer' : 'Add Customer'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Customer Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="e.g., John Doe"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  data-testid="customer-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Mobile Number *</label>
                <input
                  type="text"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  required
                  placeholder="e.g., 9876543210"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  data-testid="mobile-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="e.g., customer@email.com"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  data-testid="email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">GST Number</label>
                <input
                  type="text"
                  name="gst_number"
                  value={formData.gst_number}
                  onChange={handleChange}
                  placeholder="For B2B customers"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  data-testid="gst-input"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  rows={2}
                  placeholder="Customer address"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  data-testid="address-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Pricing Level</label>
                <select
                  name="pricing_level"
                  value={formData.pricing_level}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  data-testid="pricing-level-select"
                >
                  <option value={1}>Level 1 (Default)</option>
                  <option value={2}>Level 2</option>
                  <option value={3}>Level 3</option>
                  <option value={4}>Level 4</option>
                  <option value={5}>Level 5</option>
                </select>
                <p className="mt-1 text-xs text-slate-500">Determines product pricing for this customer</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Opening Balance (₹)
                  {formData.balance_locked && (
                    <span className="ml-2 text-xs text-amber-600 font-normal">🔒 Locked</span>
                  )}
                </label>
                <input
                  type="number"
                  name="opening_balance"
                  value={formData.opening_balance}
                  onChange={handleChange}
                  disabled={formData.balance_locked}
                  placeholder="0"
                  step="0.01"
                  min="0"
                  className={`w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent ${
                    formData.balance_locked ? 'bg-slate-100 cursor-not-allowed' : ''
                  }`}
                  data-testid="opening-balance-input"
                />
                <p className="mt-1 text-xs text-slate-500">
                  {formData.balance_locked 
                    ? 'Opening balance is locked after credit transactions' 
                    : 'Initial credit amount owed by customer. Gets locked after first credit sale.'}
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                className="px-6 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
                data-testid="save-customer"
              >
                {editingId ? 'Update' : 'Add'} Customer
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
                data-testid="cancel-button"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Customers List */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold text-slate-600">Customer</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-slate-600">Mobile</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-slate-600">Email</th>
                <th className="px-6 py-3 text-center text-sm font-semibold text-slate-600">Level</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-600">Balance</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {customers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-slate-500">
                    No customers found.
                  </td>
                </tr>
              ) : (
                customers.map(customer => (
                  <tr key={customer.id} className="hover:bg-slate-50" data-testid={`customer-${customer.id}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-800">{customer.name}</span>
                        {customer.is_guest && (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">Guest</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-600">{customer.mobile}</td>
                    <td className="px-6 py-4 text-slate-600">{customer.email || '-'}</td>
                    <td className="px-6 py-4 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        customer.pricing_level === 1 ? 'bg-slate-100 text-slate-700' :
                        customer.pricing_level === 2 ? 'bg-blue-100 text-blue-700' :
                        customer.pricing_level === 3 ? 'bg-green-100 text-green-700' :
                        customer.pricing_level === 4 ? 'bg-amber-100 text-amber-700' :
                        'bg-purple-100 text-purple-700'
                      }`}>
                        L{customer.pricing_level || 1}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className={`font-semibold ${
                        (customer.current_balance || 0) > 0 ? 'text-red-600' : 'text-green-600'
                      }`}>
                        ₹{(customer.current_balance || 0).toFixed(2)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      {!customer.is_guest && (
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => navigate(`/gposp/settings/customers/${customer.id}/prices`)}
                            className="text-emerald-600 hover:text-emerald-700 text-sm px-2 py-1 bg-emerald-50 rounded"
                            data-testid={`prices-${customer.id}`}
                            title="Set custom prices for this customer"
                          >
                            Prices
                          </button>
                          <button
                            onClick={() => handleEdit(customer)}
                            className="text-violet-600 hover:text-violet-700"
                            data-testid={`edit-${customer.id}`}
                          >
                            Edit
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reset Balances Modal */}
      {showResetModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="reset-balances-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-800">Reset Customer Balances</h2>
                <p className="text-xs text-slate-500">Sets current + all-time balance to 0. Owner-only.</p>
              </div>
              <button
                onClick={() => { setShowResetModal(false); setResetConfirmOpen(false); }}
                className="p-2 hover:bg-slate-100 rounded-lg"
                data-testid="reset-modal-close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-5">
              {/* Scope */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Apply to</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setResetScope('all')}
                    className={`p-3 rounded-lg border text-left ${resetScope === 'all' ? 'border-violet-500 bg-violet-50' : 'border-slate-200 hover:bg-slate-50'}`}
                    data-testid="reset-scope-all"
                  >
                    <div className="font-medium text-slate-800">All customers</div>
                    <div className="text-xs text-slate-500">{nonGuestCustomers.length} non-guest customer(s)</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setResetScope('selected')}
                    className={`p-3 rounded-lg border text-left ${resetScope === 'selected' ? 'border-violet-500 bg-violet-50' : 'border-slate-200 hover:bg-slate-50'}`}
                    data-testid="reset-scope-selected"
                  >
                    <div className="font-medium text-slate-800">Selected only</div>
                    <div className="text-xs text-slate-500">{resetSelectedCount} selected</div>
                  </button>
                </div>
              </div>

              {/* Selection list */}
              {resetScope === 'selected' && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <input
                      type="text"
                      placeholder="Search name or mobile..."
                      value={resetSearch}
                      onChange={(e) => setResetSearch(e.target.value)}
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
                      data-testid="reset-search-input"
                    />
                    <div className="flex gap-2 ml-3 text-sm">
                      <button type="button" onClick={selectAllReset} className="text-violet-600 hover:underline">Select all</button>
                      <span className="text-slate-300">|</span>
                      <button type="button" onClick={deselectAllReset} className="text-slate-600 hover:underline">Clear</button>
                    </div>
                  </div>
                  <div className="max-h-56 overflow-y-auto border border-slate-200 rounded-lg divide-y">
                    {resetFilteredCustomers.length === 0 ? (
                      <div className="p-4 text-center text-slate-400 text-sm">No customers</div>
                    ) : resetFilteredCustomers.map(c => (
                      <label key={c.id} className="flex items-center justify-between px-3 py-2 hover:bg-slate-50 cursor-pointer">
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={!!resetSelectedIds[c.id]}
                            onChange={() => toggleResetSelect(c.id)}
                            data-testid={`reset-select-${c.id}`}
                          />
                          <div>
                            <div className="text-sm font-medium text-slate-800">{c.name}</div>
                            <div className="text-xs text-slate-500">{c.mobile}</div>
                          </div>
                        </div>
                        <div className={`text-sm font-semibold ${(c.current_balance || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          ₹{(c.current_balance || 0).toFixed(2)}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Mode */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Reset method</label>
                <div className="space-y-2">
                  <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${resetMode === 'adjustment' ? 'border-violet-500 bg-violet-50' : 'border-slate-200'}`}>
                    <input
                      type="radio"
                      name="reset-mode"
                      checked={resetMode === 'adjustment'}
                      onChange={() => setResetMode('adjustment')}
                      className="mt-1"
                      data-testid="reset-mode-adjustment"
                    />
                    <div>
                      <div className="text-sm font-medium text-slate-800">Post adjustment entry (audit-friendly)</div>
                      <div className="text-xs text-slate-500">Keeps ledger history intact. Adds one "BALANCE_RESET" row that nets the balance to 0.</div>
                    </div>
                  </label>
                  <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${resetMode === 'wipe' ? 'border-red-500 bg-red-50' : 'border-slate-200'}`}>
                    <input
                      type="radio"
                      name="reset-mode"
                      checked={resetMode === 'wipe'}
                      onChange={() => setResetMode('wipe')}
                      className="mt-1"
                      data-testid="reset-mode-wipe"
                    />
                    <div>
                      <div className="text-sm font-medium text-red-700">Wipe ledger + opening balance (clean slate)</div>
                      <div className="text-xs text-red-600">Deletes every ledger row for the target customer(s) and sets opening_balance to 0. Cannot be undone.</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Clear pending */}
              <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200">
                <input
                  type="checkbox"
                  checked={resetClearPending}
                  onChange={(e) => setResetClearPending(e.target.checked)}
                  className="mt-1"
                  data-testid="reset-clear-pending"
                />
                <div>
                  <div className="text-sm font-medium text-slate-800">Clear pending payment collections</div>
                  <div className="text-xs text-slate-500">Deletes any unapproved/approved pending payment rows for the target customer(s).</div>
                </div>
              </label>
            </div>

            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between gap-3">
              <div className="text-sm text-slate-600">
                Will reset <span className="font-semibold text-slate-800">{resetTargetCount}</span> customer(s)
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowResetModal(false); setResetConfirmOpen(false); }}
                  className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 text-sm"
                  data-testid="reset-cancel-button"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setResetConfirmOpen(true)}
                  disabled={resetTargetCount === 0 || resetSubmitting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm font-medium"
                  data-testid="reset-submit-button"
                >
                  Reset {resetTargetCount || ''} customer(s)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      {resetConfirmOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" data-testid="reset-confirm-dialog">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M4.93 19h14.14a2 2 0 001.75-2.97l-7.07-12.72a2 2 0 00-3.5 0L3.18 16.03A2 2 0 004.93 19z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-800">Are you sure?</h3>
                <p className="text-sm text-slate-600 mt-1">
                  This will reset the current + all-time balance to ₹0 for{' '}
                  <span className="font-semibold">{resetTargetCount} customer(s)</span> using the{' '}
                  <span className="font-semibold">{resetMode === 'wipe' ? 'wipe ledger' : 'adjustment entry'}</span> method
                  {resetClearPending ? ', and clear their pending payments' : ''}.
                  {resetMode === 'wipe' && ' This cannot be undone.'}
                </p>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setResetConfirmOpen(false)}
                className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 text-sm"
                data-testid="reset-confirm-cancel"
              >
                Cancel
              </button>
              <button
                onClick={submitReset}
                disabled={resetSubmitting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm font-medium"
                data-testid="reset-confirm-yes"
              >
                {resetSubmitting ? 'Resetting...' : 'Yes, reset balances'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerSettings;
